import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronsUpDown,
  FileText,
  Folder,
  Home,
  Layers,
  LayoutPanelLeft,
  Plus,
  Receipt,
  ShoppingCart,
} from 'lucide-react';
import type { Project } from '@etap/shared';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import styles from './DesktopSidebar.module.css';

interface User {
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  features?: { attachments?: boolean };
}

interface Props {
  user: User | null;
}

/**
 * Постоянная левая навигация для десктопа (>=1024px).
 * На мобильных не рендерится — там остаётся плавающий TabBar.
 */
export function DesktopSidebar({ user }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  const { data: projects = [] } = useQuery({
    queryKey: queryKeys.projects.all,
    queryFn: () => api.getProjects(),
  });

  const { data: activeData } = useQuery({
    queryKey: queryKeys.projects.active,
    queryFn: () => api.getActiveProject().catch(() => ({ projectId: null })),
  });

  const activeProjectId = activeData?.projectId ?? projects[0]?.id ?? null;
  const activeProject = projects.find((p) => p.id === activeProjectId) ?? null;
  const isFinance = (activeProject?.type ?? 'RENOVATION') === 'FINANCE';

  const { data: unreadReports } = useQuery({
    queryKey: queryKeys.reports.unreadCount(activeProjectId ?? ''),
    queryFn: ({ queryKey }) => api.getUnreadReportsCount(queryKey[1] as string),
    enabled: !!activeProjectId,
  });
  const unreadCount = unreadReports?.count ?? 0;

  const selectProject = useMutation({
    mutationFn: (projectId: string) => api.setActiveProject(projectId),
    onMutate: (projectId) => {
      qc.setQueryData(queryKeys.projects.active, { projectId });
      setSwitcherOpen(false);
    },
  });

  useEffect(() => {
    if (!switcherOpen) return;
    const onClick = (event: MouseEvent) => {
      if (!switcherRef.current?.contains(event.target as Node)) setSwitcherOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSwitcherOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [switcherOpen]);

  const path = location.pathname;
  const inProject = (section: string) =>
    new RegExp(`^/projects/[^/]+/${section}(?:$|/)`).test(path);

  const projectPath = (section: string) =>
    activeProjectId ? `/projects/${activeProjectId}/${section}` : '/';

  const initials =
    ((user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '')).toUpperCase() ||
    user?.username?.[0]?.toUpperCase() ||
    '?';
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.username ||
    t('profile.title');

  const navItems = [
    { key: 'home', label: t('tabs.dashboard'), icon: Home, to: '/', active: path === '/' },
    ...(isFinance
      ? []
      : [
          {
            key: 'stages',
            label: t('tabs.stages'),
            icon: Layers,
            to: projectPath('stages'),
            active: inProject('stages'),
          },
        ]),
    {
      key: 'expenses',
      label: t('tabs.expenses'),
      icon: Receipt,
      to: projectPath('expenses'),
      active: inProject('expenses') && !path.endsWith('/expenses/new'),
    },
    ...(isFinance
      ? []
      : [
          {
            key: 'materials',
            label: t('dashboard.shoppingList'),
            icon: ShoppingCart,
            to: projectPath('materials'),
            active: inProject('materials'),
          },
          {
            key: 'plans',
            label: t('plans.title'),
            icon: LayoutPanelLeft,
            to: projectPath('plans'),
            active: inProject('plans'),
          },
        ]),
    ...(user?.features?.attachments
      ? [
          {
            key: 'files',
            label: t('files.title'),
            icon: Folder,
            to: projectPath('files'),
            active: inProject('files'),
          },
        ]
      : []),
    {
      key: 'reports',
      label: t('reports.title'),
      icon: FileText,
      to: activeProjectId ? `/reports?projectId=${activeProjectId}` : '/reports',
      active: path.startsWith('/reports'),
      badge: unreadCount,
    },
  ];

  return (
    <aside className={styles.sidebar}>
      <button type="button" className={styles.logo} onClick={() => navigate('/')}>
        <img className={styles.logoMark} src="/icons/icon-192.png" alt="" />
        <span className={styles.logoText}>etap</span>
      </button>

      <div className={styles.switcherWrap} ref={switcherRef}>
        <button
          type="button"
          className={styles.switcher}
          onClick={() => setSwitcherOpen((open) => !open)}
          aria-haspopup="listbox"
          aria-expanded={switcherOpen}
        >
          <span className={styles.switcherInfo}>
            <span className={styles.switcherLabel}>{t('dashboard.activeProject')}</span>
            <span className={styles.switcherTitle}>
              {activeProject?.title ?? t('dashboard.chooseProject')}
            </span>
          </span>
          <ChevronsUpDown size={16} className={styles.switcherIcon} />
        </button>

        {switcherOpen && (
          <div className={styles.switcherMenu} role="listbox">
            {projects.map((project: Project) => (
              <button
                key={project.id}
                type="button"
                role="option"
                aria-selected={project.id === activeProjectId}
                className={styles.switcherItem}
                onClick={() => selectProject.mutate(project.id)}
              >
                <span className={styles.switcherItemTitle}>{project.title}</span>
                {project.id === activeProjectId && (
                  <Check size={15} className={styles.switcherCheck} />
                )}
              </button>
            ))}
            <div className={styles.switcherDivider} />
            <button
              type="button"
              className={styles.switcherItem}
              onClick={() => {
                setSwitcherOpen(false);
                navigate('/projects/new');
              }}
            >
              <span className={styles.switcherItemTitle}>{t('dashboard.newProject')}</span>
              <Plus size={15} className={styles.switcherCheck} />
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.addBtn}
        onClick={() =>
          navigate(
            activeProjectId ? `/projects/${activeProjectId}/expenses/new` : '/projects/new',
            { state: { entry: 'sidebar' } },
          )
        }
      >
        <Plus size={18} strokeWidth={2.5} />
        {t('expenses.add')}
      </button>

      <nav className={styles.nav} aria-label={t('tabs.dashboard')}>
        {navItems.map(({ key, label, icon: Icon, to, active, badge }) => (
          <button
            key={key}
            type="button"
            className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
            onClick={() => navigate(to)}
            aria-current={active ? 'page' : undefined}
          >
            <Icon size={18} strokeWidth={2} />
            <span className={styles.navLabel}>{label}</span>
            {!!badge && badge > 0 && <span className={styles.navBadge}>{badge}</span>}
          </button>
        ))}
      </nav>

      <button
        type="button"
        className={`${styles.profile} ${path.startsWith('/profile') ? styles.profileActive : ''}`}
        onClick={() => navigate('/profile')}
      >
        <span className={styles.avatar}>{initials}</span>
        <span className={styles.profileName}>{displayName}</span>
      </button>
    </aside>
  );
}
